
import { Box, Divider, Fab, Grid, Grow, Icon, Paper, Stack, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import AddIcon from '@mui/icons-material/Add';
import RemovetIcon from '@mui/icons-material/Remove';
import { ColorlibConnector, ColorlibStepIcon } from "../../components/reusable/StyledStepper";
import StyledImageButton from "../../components/reusable/StyledImageButton";
import { IndividualItems, MainExtras, MainOptions, ProteinOptions, SaladOptions, Sauces, SideExtras, SideOptions, SideSalads, SweetsTreats } from "../../constants/constants";
import StyledButton from "../../components/reusable/StyledButton";
import { useConfetti } from "../../hooks/useConfetti";
import confetti from "canvas-confetti";
import { ConePath, PitaPath, SouvPath } from "../../constants/svgPaths";
import StyledDialog from "../../components/reusable/StyledDialog";
import { TMenuOptionProps } from "../../constants/types";
import GyroShot from '../../images/demo-pics/gyroshot.avif'
import PlasterTexture from '../../images/white_plaster.avif'
import StuccoTexture from '../../images/stucco.avif';

const useStyles = makeStyles()((theme) => ({
  menuBody: {
    backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6)), url(${PlasterTexture})`,
    backgroundSize: '50%',
    backgroundPosition: 'center',
    backgroundRepeat: 'repeat'
  },
  menuItemPaper: {
    borderRadius: 12, 
    overflow: 'hidden', 
    width: '100%', 
    transition: 'transform .22s ease, box-shadow .22s ease', 
    '&:hover': { 
      transform: 'translateY(-6px)', 
      boxShadow: '0 18px 30px rgba(0,0,0,0.18)'
  
    }
  },
  containerBodyBlue: {
    background: `linear-gradient(rgba(127, 183, 251, 0.6), rgba(127, 183, 251, 0.6)), url(${StuccoTexture})`, 
    backgroundSize: '20%', 
    backgroundPosition: 'center', 
    borderTop: '2px solid rgba(127, 183, 251, 0.8)',
    borderBottom: '2px solid rgba(127, 183, 251, 0.8)'
  },
  containerBodyYellow: {
    background: `linear-gradient(rgba(255, 212, 80, 0.6), rgba(255, 212, 80, 0.6)), url(${StuccoTexture})`, 
    backgroundSize: '20%', 
    backgroundPosition: 'center', 
    borderTop: '2px solid rgba(255, 212, 80, 0.8)', 
    borderBottom: '2px solid rgba(255, 212, 80, 0.8)', 
  }
}))

  const cone = confetti.shapeFromPath({
    path: ConePath
  })
  const souv = confetti.shapeFromPath({
    path: SouvPath
  })
  const pita = confetti.shapeFromPath({
    path: PitaPath
  })


export default function Menu() {
  const { classes } = useStyles();
  const [activeStep, setActiveStep] = useState<number>(0)
  const [selectedItems, setSelectedItems] = useState<{main: TMenuOptionProps | null, protein: TMenuOptionProps | null, sides: Array<TMenuOptionProps>}>({main: null, protein: null, sides: []})
  const [itemDetails, setItemDetails] = useState<TMenuOptionProps | null>(null)
  const [sectionVisibility, setSectionVisibility] = useState<Array<boolean>>([false, false, false])
  const [fullMenu, setFullMenu] = useState<boolean>(true)
  const { fire: fire1 } = useConfetti(
    {
      duration: 2000,
      particleCount: 2,
      interval: 60,
      origin: {
        x: 0,
        y: 0.5
      },
      angle: 60,
      spread: 35,
      startVelocity: 35,
      gravity: 1.2,
      scalar: 3,
      shapes: [cone, souv, pita],
      colors: ['#165FAB', '#60A5FA', '#ffee03'],
    }
  )
  const { fire: fire2 } = useConfetti(
    {
      duration: 2000,
      particleCount: 2,
      interval: 60,
      origin: {
        x: 1,
        y: 0.5
      },
      angle: 120,
      spread: 35,
      startVelocity: 35,
      gravity: 1.2,
      scalar: 3,
      shapes: [cone, souv, pita],
      colors: ['#165FAB', '#60A5FA', '#ffee03'],
    }
  )

  useEffect(() => {
    if (selectedItems.sides.length == 2) {
      setActiveStep(3) // zero-based index
      setSectionVisibility([false, false, false])

    }
  }, [selectedItems])

  useEffect(() => {
    if (activeStep === 3) {
      
      fire1()
      fire2()
    }
  }, [activeStep])

  return (
    <Box className={classes.menuBody}>
    
      <Grid container spacing={{xs: 2, md: 8}} sx={{background: 'white', borderBottom: '1px solid rgba(0,0,0,0.12)'}}>
        <Grid 
          size={{xs:12}} 
          sx={{
            textAlign: 'center',
            position: 'relative',
            paddingTop: 18,
            paddingBottom: 18,
            '&:before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundImage: `linear-gradient(90deg, rgba(255, 255, 255, 0.7)10%, rgba(255, 255, 255, 0.5), 30%, rgba(255, 255, 255, 0.3)), url(${GyroShot})`,
              backgroundSize: '700px 700px',
              backgroundPosition: 'right -200px top -150px',
              backgroundRepeat: 'no-repeat',
              // transform: 'scaleX(-1)',
              zIndex: 0,
            },
            '& > *': {
              position: 'relative',
              zIndex: 1,
            },
          }}
        >
          <Typography variant="h1" sx={{paddingBottom: 3, textAlign: 'center', textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)', textTransform: 'uppercase'}}>Meet Your Gyros</Typography>

          <Grid size={{xs: 10, lg: 3}} offset={{xs: 1, lg: 8}} >
            <StyledButton 
              onClick={() => {
                if (fullMenu) {
                  setActiveStep(0)
                  setFullMenu(false)
                  setSectionVisibility([true, false, false])
                  setSelectedItems({main: null, protein: null, sides: []})
                } else {
                  setActiveStep(0)
                  setFullMenu(true)
                  setSectionVisibility([false, false, false])
                  setSelectedItems({main: null, protein: null, sides: []})
                }
              }}
              fullWidth={true}
              variant='contained'
              size="large"
            >
              {
                fullMenu
                  ?
                <Typography variant="button" sx={{fontWeight: 'bold'}}>Build your Box</Typography>
                  :
                <Typography variant="button" sx={{fontWeight: 'bold'}}>See our whole menu</Typography>
              }
            </StyledButton>
          </Grid>
        </Grid>

      </Grid>

        {
          !fullMenu
            &&
          <>
            <Grid container spacing={{xs: 2, md: 8}} sx={{padding: {xs: 3, md: 5}}} >
              <Grid size={{xs: 12}} >
                <Stack>
                  <Stepper alternativeLabel activeStep={activeStep} connector={<ColorlibConnector/>}>

                    <Step 
                      key={1}
                      onClick={() => activeStep > 0 && (setActiveStep(0), setSectionVisibility([true, false, false]), setSelectedItems({...selectedItems, sides: []}))}
                    >
                      <StepLabel
                        slots={{stepIcon: ColorlibStepIcon}} 
                      >
                        <Typography variant='h3'>FIRST</Typography>
                      </StepLabel>
                    </Step>

                    <Step 
                      key={2}
                      onClick={() => activeStep > 1 && (setActiveStep(1), setSectionVisibility([false, true, false]), setSelectedItems({...selectedItems, sides: []}))}
                    >
                      <StepLabel 
                        slots={{stepIcon: ColorlibStepIcon}} 
                      >
                        <Typography variant='h3'>THEN</Typography>
                      </StepLabel>
                    </Step>

                    <Step 
                      key={3}
                      onClick={() => activeStep > 2 && (setActiveStep(2), setSectionVisibility([false, false, true]), setSelectedItems({...selectedItems, sides: []}))}
                    >
                      <StepLabel 
                        slots={{stepIcon: ColorlibStepIcon}} 
                      >
                        <Typography variant='h3'>FINALLY</Typography>
                      </StepLabel>
                    </Step>

                  </Stepper>
                </Stack>
              </Grid>
              <Grid size={{xs: 12}} sx={{textAlign: 'center'}}>
                <Typography variant="h2">
                  {
                    activeStep === 0
                      ?
                      <>
                        <Typography variant="h2" sx={{textDecoration: 'underline'}}>GYROSA SIGNATURES</Typography>
                        PICK YOUR MEAL
                      </>
                      : activeStep === 1
                        ?
                      "PICK YOUR PROTEIN"
                        : activeStep === 2
                          ?
                        "PICK TWO SIDES"
                          :
                        "YOUR MEAL"
                  }
                </Typography>
              </Grid>
            </Grid>
          </>
        }
        
        {/* Grids for STEP 1  */}
        {
          sectionVisibility[0]
            &&
            <>
              <Grid container spacing={{xs: 2, md: 8}} sx={{padding: {xs: 3, md: 5}}} >
                <Grid size={{xs: 0, md: 2}}>
                  </Grid>
                  {
                    MainOptions.map((e, index) => (
                      <Grow
                        key={e.title}
                        onExited={() => index === MainOptions.length -1 && setSectionVisibility([false, true, false])} // DO ON THE LAST TRANSITION IN THE CHAIN
                        in={activeStep === 0}
                        style={{ transformOrigin: '0 0 0' }}
                            {...(activeStep === 0 ?  { timeout: 1000 + index *  500 } : {})}
                      >
                        <Grid size={{xs: 12, md: 4}}>
                          <Paper elevation={12} className={classes.menuItemPaper}>
                            <StyledImageButton url={e.src} title={e.title} onClick={() => (setActiveStep(1), setSelectedItems({...selectedItems, main: e}))}/>
                          </Paper>
                        </Grid>
                      </Grow>
                    ))
                  }
                  <Grid size={{xs: 0, md: 2}}>
                </Grid>
              </Grid>
            </>
        }

        {/* Grids for STEP 2 */}
        {
          sectionVisibility[1]
            &&
          <Grid container spacing={{xs: 2, md: 8}} sx={{padding: {xs: 3, md: 5}}} >
            {
              ProteinOptions.map((e, index) => (
                <Grow
                  key={e.title}
                  in={activeStep === 1}
                  style={{ transformOrigin: '0 0 0' }}
                  {...(activeStep === 1 ? { timeout: 600 + index * 600 } : {})}
                  onExited={() => index === MainOptions.length -1 && setSectionVisibility([false, false, true])} // DO ON THE LAST TRANSITION IN THE CHAIN
                    
                >
                  <Grid size={{xs: 6, md: 4}}>
                    <Paper elevation={12} className={classes.menuItemPaper}>
                      <StyledImageButton url={e.src} title={e.title} onClick={() => (setActiveStep(2), setSelectedItems({...selectedItems, protein: e}))}/>
                    </Paper>
                  </Grid>
                </Grow>
              ))
            }
          </Grid>
        }

        {/* Grids for STEP 3 */}
        {
          sectionVisibility[2]
            &&
          <Grid container spacing={{xs: 2, md: 8}} sx={{padding: {xs: 3, md: 5}}} >
            {
              [...SideOptions, ...SideSalads].map((e, index) => (
                <Grow
                  key={e.title}
                  in={activeStep === 2}
                  style={{ transformOrigin: '0 0 0' }}
                  {...(activeStep === 2 ? { timeout: 600 + index * 400 } : {})}
                  onExited={() => index === MainOptions.length -1 && setSectionVisibility([false, false, true])} // DO ON THE LAST TRANSITION IN THE CHAIN
                    
                >
                  <Grid size={{xs: 6, md: 4}}  sx={{mx: 'auto'}}>
                    <Paper elevation={12} className={classes.menuItemPaper}>
                      <StyledImageButton  url={e.src} title={e.title} onClick={() => {
                        !selectedItems.sides.includes(e) && setSelectedItems({...selectedItems, sides: [...selectedItems.sides, e]})
                      }}/>
                      {
                        selectedItems.sides.includes(e)
                          &&
                          <>
                            <Fab
                              color='primary'
                              sx={[
                                {
                                  position: 'absolute', top: '3%', right: '3%', 
                                },
                                (theme) => ({
                                  [theme.breakpoints.down('md')]: {
                                    transform: 'scale(0.8)',
                                    top: '0%',
                                    right: '0%'
                                  },
                                  [theme.breakpoints.down('sm')]: {
                                    transform: 'scale(0.5)',
                                    top: '-3%',
                                    right: '-3%'
                                  },
    
                                })
    
                              ]}
                              onClick={() => setSelectedItems({...selectedItems, sides: [...selectedItems.sides, e]})}
                            >
                              <AddIcon/>
                            </Fab>
                            <Fab
                              color='primary' 
                              sx={[
                                {
                                  position: 'absolute', top: '20%', right: '3%', 
                                },
                                (theme) => ({
                                  [theme.breakpoints.down('md')]: {
                                    transform: 'scale(0.8)',
                                    top: '25%',
                                    right: '0%'
                                  },
                                  [theme.breakpoints.down('sm')]: {
                                    transform: 'scale(0.5)',
                                    top: '30%',
                                    right: '-3%'
                                  },
    
                                })
    
                              ]}                          
                              onClick={() => setSelectedItems({...selectedItems, sides: selectedItems.sides.filter((n: any) => n !== e)})}
                            >
                              <RemovetIcon/>
                            </Fab>
                          </>
                      }
                    </Paper>
                  </Grid>
                </Grow>
              ))
            }
          </Grid>
        }

        {/* Final Meal Selection */}
        {
          activeStep === 3
            &&
            <Grid container spacing={{xs: 2, md: 8}} sx={{padding: {xs: 3, md: 5}}} >
              <Grow 
                in={activeStep === 3}
                style={{ transformOrigin: '0 0 0' }}
                {...(activeStep === 3 ? { timeout: 600 } : {})}
                // onExited={() => index === MainOptions.length -1 && setSectionVisibility([false, false, true])} // DO ON THE LAST TRANSITION IN THE CHAIN
              >
                <Grid size={{xs: 6, md: 3}}  sx={{mx: 'auto'}}>
                  <Paper elevation={12} className={classes.menuItemPaper} >
                    <StyledImageButton  url={selectedItems?.main?.src || ''} title={selectedItems?.main?.title || ''}/>
                  </Paper>
                </Grid>
              </Grow>
              <Grow 
                in={activeStep === 3}
                style={{ transformOrigin: '0 0 0' }}
                {...(activeStep === 3 ? { timeout: 800 } : {})}
                // onExited={() => index === MainOptions.length -1 && setSectionVisibility([false, false, true])} // DO ON THE LAST TRANSITION IN THE CHAIN
              >
                <Grid size={{xs: 6, md: 3}}  sx={{mx: 'auto'}}>
                  <Paper elevation={12} className={classes.menuItemPaper} >
                    <StyledImageButton  url={selectedItems?.protein?.src || ''} title={selectedItems?.protein?.title || ''}/>
                  </Paper>
                </Grid>
              </Grow>
              <Grow 
                in={activeStep === 3}
                style={{ transformOrigin: '0 0 0' }}
                {...(activeStep === 3 ? { timeout: 1000 } : {})}
                // onExited={() => index === MainOptions.length -1 && setSectionVisibility([false, false, true])} // DO ON THE LAST TRANSITION IN THE CHAIN
              >
                <Grid size={{xs: 6, md: 3}}  sx={{mx: 'auto'}}>
                  <Paper elevation={12} className={classes.menuItemPaper} >
                    <StyledImageButton  url={selectedItems.sides[0].src} title={selectedItems.sides[0].title}/>
                  </Paper>
                </Grid>
              </Grow>
              <Grow 
                in={activeStep === 3}
                style={{ transformOrigin: '0 0 0' }}
                {...(activeStep === 3 ? { timeout: 1200 } : {})}
                // onExited={() => index === MainOptions.length -1 && setSectionVisibility([false, false, true])} // DO ON THE LAST TRANSITION IN THE CHAIN
              >
                <Grid size={{xs: 6, md: 3}}  sx={{mx: 'auto'}}>
                  <Paper elevation={12} className={classes.menuItemPaper} >
                    <StyledImageButton  url={selectedItems.sides[1].src} title={selectedItems.sides[1].title}/>
                  </Paper>
                </Grid>
              </Grow>
            </Grid>
        }

        {/* Whole Menu */}
        {
          fullMenu
            &&
            <>
              {/* MAINS */}
              <Grid container spacing={{xs: 2, md: 8}} sx={{padding: 3}} >
                <Grid size={{xs: 12}} >
                  <Typography variant='h2' sx={{textTransform: 'uppercase', letterSpacing: 2, mb: 1}}>Mains</Typography>
                  <Divider sx={{borderColor: 'rgba(0,0,0,0.12)', mb: 3}}/>
                </Grid>
                {
                  MainOptions.map(e => (
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 2}} key={e.title} sx={{display: 'flex'}}>
                      <Paper elevation={4} className={classes.menuItemPaper}>
                        <StyledImageButton url={e.src} title={e.title} onClick={() => setItemDetails(e)} />
                      </Paper>
                    </Grid>
                  ))
                }
                {
                  MainExtras.map(e => (
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 2}} key={e.title} sx={{display: 'flex'}}>
                      <Paper elevation={4} className={classes.menuItemPaper}>
                        <StyledImageButton url={e.src} title={e.title} onClick={() => setItemDetails(e)} />
                      </Paper>
                    </Grid>
                  ))
                }
              </Grid>

              {/* SALADS & SUCH */}
              <Grid container spacing={{xs: 2, md: 8}} sx={{padding: 3}} className={classes.containerBodyBlue}>
                <Grid size={{xs: 12}} >
                  <Typography variant='h2' sx={{textTransform: 'uppercase', letterSpacing: 2, mb: 1, textAlign: 'right'}}>Salads & Such</Typography>
                  <Divider sx={{borderColor: 'rgba(0,0,0,0.12)', mb: 3}}/>
                </Grid>
                {
                  SaladOptions.map((e, index) => (
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 2}} key={e.title} sx={{display:'flex', ml: index === 0 ? 'auto' : 'none'}}>
                      <Paper elevation={4} className={classes.menuItemPaper}>
                        <StyledImageButton url={e.src} title={e.title} onClick={() => setItemDetails(e)} />
                      </Paper>
                    </Grid>
                  ))
                }
                {
                  SideSalads.map((e, index) => (
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 2}} key={e.title} sx={{display:'flex'}}>
                      <Paper elevation={4} className={classes.menuItemPaper}>
                        <StyledImageButton url={e.src} title={e.title} onClick={() => setItemDetails(e)} />
                      </Paper>
                    </Grid>
                  ))
                }
              </Grid>

              {/* THIS & THAT */}
              <Grid container spacing={{xs: 2, md: 8}} sx={{padding: 3}} >
                <Grid size={{xs: 12}} >
                  <Typography variant='h2' sx={{textTransform: 'uppercase', letterSpacing: 2, mb: 1}}>This & That</Typography>
                  <Divider sx={{borderColor: 'rgba(0,0,0,0.12)', mb: 3}}/>
                </Grid>
                {
                  IndividualItems.map((e, index) => (
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 2}} key={e.title} sx={{display:'flex'}}>
                      <Paper elevation={4} className={classes.menuItemPaper}>
                        <StyledImageButton url={e.src} title={e.title} onClick={() => setItemDetails(e)} />
                      </Paper>
                    </Grid>
                  ))
                }
                {
                  SideOptions.map((e, index) => (
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 2}} key={e.title} sx={{display:'flex'}}>
                      <Paper elevation={4} className={classes.menuItemPaper}>
                        <StyledImageButton url={e.src} title={e.title} onClick={() => setItemDetails(e)} />
                      </Paper>
                    </Grid>
                  ))
                }
                {
                  SideExtras.map(e => (
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 2}} key={e.title} sx={{display:'flex'}}>
                      <Paper elevation={4} className={classes.menuItemPaper}>
                        <StyledImageButton url={e.src} title={e.title} onClick={() => setItemDetails(e)} />
                      </Paper>
                    </Grid>
                  ))
                }
              </Grid>

              {/* PROTEIN OPTIONS */}
              <Grid container spacing={{xs: 2, md: 8}} sx={{padding: 3}} className={classes.containerBodyYellow}>
                <Grid size={{xs: 12}} >
                  <Typography variant='h2' sx={{textTransform: 'uppercase', letterSpacing: 2, mb: 1, textAlign:'right'}}>Protein Options</Typography>
                  <Divider sx={{borderColor: 'rgba(0,0,0,0.12)', mb: 3}}/>
                </Grid>
                {
                  ProteinOptions.map((e, index) => (
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 2}} key={e.title} sx={{display:'flex',  ml: index === 0 ? 'auto' : 'none'}}>
                      <Paper elevation={4} className={classes.menuItemPaper}>
                        <StyledImageButton url={e.src} title={e.title} onClick={() => setItemDetails(e)} />
                      </Paper>
                    </Grid>
                  ))
                }
              </Grid>

              {/* SWEETS & TREATS */}
              <Grid container spacing={{xs: 2, md: 8}} sx={{padding: 3}} >
                <Grid size={{xs: 12}} >
                  <Typography variant='h2' sx={{textTransform: 'uppercase', letterSpacing: 2, mb: 1}}>Sweets & Treats</Typography>
                  <Divider sx={{borderColor: 'rgba(0,0,0,0.12)', mb: 3}}/>
                </Grid>
                {
                  SweetsTreats.map(e => (
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 2}} key={e.title} sx={{display:'flex'}}>
                      <Paper elevation={4} className={classes.menuItemPaper}>
                        <StyledImageButton url={e.src} title={e.title} onClick={() => setItemDetails(e)} />
                      </Paper>
                    </Grid>
                  ))
                }
              </Grid>



              {/* SAUCES */}
              <Grid container spacing={{xs: 2, md: 8}} sx={{padding: 3}} className={classes.containerBodyBlue}>
                <Grid size={{xs: 12}} >
                  <Typography variant='h2' sx={{textTransform: 'uppercase', letterSpacing: 2, mb: 1, textAlign:'right'}}>Sauces</Typography>
                  <Divider sx={{borderColor: 'rgba(0,0,0,0.12)', mb: 3}}/>
                </Grid>
                {
                  Sauces.map((e, index) => (
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 2}} key={e.title} sx={{display:'flex', ml: index === 0 ? 'auto' : 'none'}}>
                      <Paper elevation={4} className={classes.menuItemPaper}>
                        <StyledImageButton url={e.src} title={e.title} onClick={() => setItemDetails(e)} />
                      </Paper>
                    </Grid>
                  ))
                }
              </Grid>

            </>
        }
      
      {/* Details Dialog */}
      <StyledDialog
        open={!!itemDetails}
        dialogTitle={itemDetails?.title || ''}
        onClose={() => {setItemDetails(null)}}
        maxWidth="lg"
        fullWidth={true}
      >
        <Grid container spacing={{xs: 2}} sx={{padding: {xs: 1.5, md: 1.5}}} >
          <Grid size={{xs: 12, md: 5}} sx={{maxHeight: '30vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <img src={itemDetails?.src} style={{display: 'block', width: '100%', borderRadius: '16px'}}/>
          </Grid>
          <Grid size={{xs: 1}}>
            <Divider orientation="vertical"/>
          </Grid>
          <Grid size={{xs: 12, md: 5}}>
            <Typography variant="body2">{itemDetails?.description}</Typography>
          </Grid>
        </Grid>
      </StyledDialog>
    </Box>
  )
}
